import { useMemo, useState } from 'react';
import { create } from 'zustand';
import { Check, GripVertical, Plus, Search, Trash2 } from '@/components/ui/icons';
import type { CustomField, CustomFieldType, FieldOption, Location, ViewColumn, ViewSettings } from '@shared/types';
import { BUILTIN_FIELDS, PALETTE } from '@shared/constants';
import { newId } from '@shared/ids';
import { useDb } from '@/lib/store';
import { fieldsForLocation, workspaceOfLocation } from '@/lib/hierarchy';
import { createCustomField, deleteCustomField, updateCustomField } from '@/lib/actions';
import { Icon, FIELD_TYPE_ICONS } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { Popover } from '@/components/ui/Popover';

export const FIELD_TYPES: { type: CustomFieldType; label: string }[] = [
  { type: 'dropdown', label: 'Dropdown' },
  { type: 'text', label: 'Text' },
  { type: 'textarea', label: 'Text area (Long text)' },
  { type: 'number', label: 'Number' },
  { type: 'money', label: 'Money' },
  { type: 'date', label: 'Date' },
  { type: 'labels', label: 'Labels' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'people', label: 'People' },
  { type: 'url', label: 'Website' },
  { type: 'email', label: 'Email' },
  { type: 'phone', label: 'Phone' },
  { type: 'rating', label: 'Rating' },
  { type: 'progress', label: 'Progress (manual)' },
];

export function FieldEditorModal({ location, field, onClose }: { location: Location; field?: CustomField; onClose: () => void }) {
  const db = useDb((d) => d);
  const [name, setName] = useState(field?.name ?? '');
  const [type, setType] = useState<CustomFieldType>(field?.type ?? 'dropdown');
  const [options, setOptions] = useState<FieldOption[]>(field?.options ?? []);
  const [newOpt, setNewOpt] = useState('');
  const hasOptions = type === 'dropdown' || type === 'labels';
  const save = () => {
    if (!name.trim()) return;
    if (field) updateCustomField(field.id, { name: name.trim(), options: hasOptions ? options : undefined });
    else {
      const ws = workspaceOfLocation(db, location) ?? '';
      createCustomField({
        workspaceId: ws,
        scope: location.type === 'workspace' ? { type: 'workspace', id: ws } : { type: location.type, id: location.id },
        name: name.trim(),
        type,
        options: hasOptions ? options : undefined,
        config: type === 'money' ? { currency: 'USD', precision: 2 } : type === 'rating' ? { ratingMax: 5 } : undefined,
      });
    }
    onClose();
  };
  const addOpt = () => {
    if (!newOpt.trim()) return;
    setOptions([...options, { id: newId(8), name: newOpt.trim(), color: PALETTE[(options.length * 3 + 1) % PALETTE.length], orderIndex: options.length }]);
    setNewOpt('');
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={field ? `Edit ${field.name}` : 'Create field'}
      width={480}
      footer={
        <>
          {field && (
            <button
              className="btn"
              style={{ color: 'var(--danger)', marginRight: 'auto' }}
              onClick={() => {
                if (confirm(`Delete field "${field.name}"? Values on all tasks will be removed.`)) {
                  deleteCustomField(field.id);
                  onClose();
                }
              }}
            >
              <Trash2 size={14} /> Delete field
            </button>
          )}
          <button className="btn outline" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!name.trim()} onClick={save}>
            {field ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      {!field && (
        <div className="form-field">
          <label>Field type</label>
          <div className="field-type-grid">
            {FIELD_TYPES.map((t) => {
              const I = FIELD_TYPE_ICONS[t.type];
              return (
                <button key={t.type} className={`field-type ${type === t.type ? 'on' : ''}`} onClick={() => setType(t.type)}>
                  <I size={15} />
                  <span className="truncate">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="form-field">
        <label>Field name</label>
        <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter name..." onKeyDown={(e) => e.key === 'Enter' && !hasOptions && save()} />
      </div>
      {hasOptions && (
        <div className="form-field">
          <label>Options</label>
          {options.map((o, i) => (
            <div key={o.id} className="opt-row">
              <GripVertical size={14} className="faint" />
              <Popover
                content={(close) => (
                  <div className="menu" style={{ padding: 10 }}>
                    <div className="swatches">
                      {PALETTE.map((c) => (
                        <button
                          key={c}
                          className="swatch"
                          style={{ background: c }}
                          onClick={() => {
                            setOptions(options.map((x) => (x.id === o.id ? { ...x, color: c } : x)));
                            close();
                          }}
                        >
                          {c === o.color && <Check size={12} />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              >
                <button className="opt-color" style={{ background: o.color }} />
              </Popover>
              <input className="input bare grow" value={o.name} onChange={(e) => setOptions(options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <button className="icon-btn xs" onClick={() => setOptions(options.filter((x) => x.id !== o.id))}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <div className="opt-row">
            <Plus size={14} className="faint" />
            <input className="input bare grow" placeholder="Type an option and press Enter" value={newOpt} onChange={(e) => setNewOpt(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addOpt()} />
          </div>
        </div>
      )}
    </Modal>
  );
}

const useFieldEditor = create<{ state: { location: Location; field?: CustomField } | null }>(() => ({ state: null }));
export const openFieldEditor = (location: Location, field?: CustomField) => useFieldEditor.setState({ state: { location, field } });

/** Rendered once by the app shell so the editor survives popovers closing. */
export function FieldEditorHost() {
  const state = useFieldEditor((s) => s.state);
  if (!state) return null;
  return <FieldEditorModal key={state.field?.id ?? 'new'} location={state.location} field={state.field} onClose={() => useFieldEditor.setState({ state: null })} />;
}

/** Column visibility panel ("Fields"). */
export function FieldsPanel({ location, settings, onChange }: { location: Location; settings: ViewSettings; onChange: (s: Partial<ViewSettings>) => void }) {
  const db = useDb((d) => d);
  const [q, setQ] = useState('');
  const custom = useMemo(() => fieldsForLocation(db, location), [db, location]);
  const columns = settings.columns;
  const isShown = (id: string) => columns.some((c) => c.id === id && !c.hidden);
  const toggle = (id: string) => {
    const exists = columns.find((c) => c.id === id);
    let next: ViewColumn[];
    if (!exists) next = [...columns, { id }];
    else next = columns.map((c) => (c.id === id ? { ...c, hidden: !c.hidden } : c));
    onChange({ columns: next });
  };
  const all = [
    ...BUILTIN_FIELDS.filter((f) => f.id !== 'name').map((f) => ({ id: f.id, label: f.label, icon: <Icon name={f.icon} size={15} />, cf: undefined as CustomField | undefined })),
    ...custom.map((f) => {
      const I = FIELD_TYPE_ICONS[f.type];
      return { id: `cf:${f.id}`, label: f.name, icon: <I size={15} />, cf: f };
    }),
  ].filter((f) => !q || f.label.toLowerCase().includes(q.toLowerCase()));
  const shown = all.filter((f) => isShown(f.id));
  const hidden = all.filter((f) => !isShown(f.id));
  const row = (f: (typeof all)[number]) => (
    <div key={f.id} className="fields-row" onClick={() => toggle(f.id)}>
      <span className="faint">{f.icon}</span>
      <span className="grow truncate">{f.label}</span>
      {f.cf && (
        <button
          className="icon-btn xs fields-edit"
          onClick={(e) => {
            e.stopPropagation();
            openFieldEditor(location, f.cf!);
          }}
        >
          <Icon name="Wrench" size={12} />
        </button>
      )}
      <span className="toggle" data-on={isShown(f.id)} />
    </div>
  );
  return (
    <div className="fields-panel">
      <div className="filter-head">
        <span style={{ fontWeight: 600 }}>Fields</span>
      </div>
      <div className="menu-search" style={{ margin: '0 0 6px', border: '1px solid var(--border)', borderRadius: 8 }}>
        <Search size={14} className="faint" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for new or existing fields" />
      </div>
      <button className="btn sm" style={{ width: '100%', justifyContent: 'center', margin: '4px 0 8px' }} onClick={() => openFieldEditor(location)}>
        <Plus size={14} /> Create field
      </button>
      {shown.length > 0 && <div className="menu-label">Shown</div>}
      {shown.map(row)}
      {hidden.length > 0 && <div className="menu-label">Hidden</div>}
      {hidden.map(row)}
    </div>
  );
}
